import $firebase from "firebase/compat/app";
import { apiUser } from "@/components/toLoad/api";
import { listUtils } from "@/components/toLoad/listUtils";

import _moment from "moment";

const IMAGE_QUALITIES = ["small", "medium", "high", "ultrahigh"];
const IMAGE_SIZES = {
  default: {
    square: { small: "50x50", medium: "200x200", high: "800x800", ultrahigh: "1200x1200" },
  },
  blog: {
    horizontal: { small: "1344x768", ultrahigh: "2752x1536" },
    vertical: { small: "768x1344", ultrahigh: "1536x2752" },
    square: { small: "1024x1024", ultrahigh: "2048x2048" },
  },
};
const DEFAULT_ORIENTATION = { default: "square", blog: "horizontal" };

const resolveImageSize = function(profile, quality, orientation) {
  const sizes = IMAGE_SIZES[profile] || IMAGE_SIZES.default;
  const fallback = DEFAULT_ORIENTATION[profile] || "square";
  const table = sizes[orientation || fallback] || sizes[fallback];
  const index = Math.max(IMAGE_QUALITIES.indexOf(quality), 0);
  const order = [...IMAGE_QUALITIES.slice(0, index + 1).reverse(), ...IMAGE_QUALITIES.slice(index + 1)];
  return table[order.find((q) => table[q])];
};

const findWithAttr = function(array, attr, value) {
  for (let i = 0; i < array.length; i += 1)
    if (array[i][attr] === value)
      return i;

  return -1;
};

export const functions = {
  getInitials: function(name, max = 3) {
    if (!name) return "";
    return String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, max)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  },
  /**
   * Return the link with the good size of image input
   * @param {String} path
   * @param {String|Number} quality small | medium | high | ultrahigh (default), or a raw size ("1344x768" / 50)
   * @param {{profile?: "default"|"blog", orientation?: "square"|"horizontal"|"vertical"}} options
   * @returns {String}
   */
  getImgLink: function(path, quality = "ultrahigh", options = {}) {
    const size = IMAGE_QUALITIES.includes(quality)
      ? resolveImageSize(options.profile || "default", quality, options.orientation)
      : quality;
    if (path && typeof path === "object" && typeof path.link === "string")
      path = path.link;
    if (!path || typeof path !== "string") return "";

    const sizeStr = typeof size === "number" ? `_${size}x${size}` : `_${size}`;
    const qIndex = path.indexOf("?");
    const basePath = qIndex === -1 ? path : path.slice(0, qIndex);
    const query = qIndex === -1 ? "" : path.slice(qIndex);
    const sizedWebp = /_\d+x\d+\.webp$/i;

    if (sizedWebp.test(basePath))
      return basePath.replace(sizedWebp, `${sizeStr}.webp`) + query;

    const fileName = decodeURIComponent(basePath.split("/").pop() || "");
    const extMatch = /\.([a-zA-Z0-9]+)$/.exec(fileName);
    if (!extMatch || !/^(jpe?g|png|gif|webp)$/i.test(extMatch[1])) return path;

    const extIndex = basePath.lastIndexOf(`.${extMatch[1]}`);
    if (extIndex === -1) return path;
    return `${basePath.slice(0, extIndex)}${sizeStr}.webp${query}`;
  },
  /**
   * Same as getImgLink for blog images (horizontal by default)
   * @param {String} path
   * @param {String} quality small | medium | high | ultrahigh
   * @param {String} orientation horizontal | vertical | square
   * @returns {String}
   */
  getBlogImgLink: function(path, quality = "ultrahigh", orientation = "horizontal") {
    return this.getImgLink(path, quality, { profile: "blog", orientation });
  },
  /**
   * Wait until the webp generated by the Firebase resize extension is reachable
   * @param {String} url original image link
   * @param {{profile?: "default"|"blog", delay?: Number, maxAttempts?: Number}} options
   * @returns {Promise<Boolean>} true if the resized image is available
   */
  async waitForResizedImage(url, options = {}) {
    const { profile = "default", delay = 1500, maxAttempts = 40 } = options;
    const resized = profile === "blog"
      ? this.getBlogImgLink(url, "ultrahigh")
      : this.getImgLink(url);
    if (!resized || resized === url) return true;

    const probe = (src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = `${src}${src.includes("?") ? "&" : "?"}probe=${Date.now()}`;
    });

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (await probe(resized)) return true;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    return false;
  },
  /**
   * Normalize tags stored as an array, a JSON array string or a "," / ";" separated string
   * @param {Array|String} value
   * @returns {String[]}
   */
  parseTags(value) {
    let list = value;
    if (typeof list === "string") {
      const raw = list.trim();
      if (raw.startsWith("["))
        try {
          list = JSON.parse(raw);
        } catch {
          list = raw.replace(/^\[|\]$/g, "").split(/[,;]/);
        }
      else
        list = raw.split(/[,;]/);
    }
    if (!Array.isArray(list)) return [];
    const cleaned = list
      .map(tag => String(tag).trim().replace(/^["']|["']$/g, "").trim())
      .filter(Boolean);
    return [...new Set(cleaned)];
  },
  resolveMediaUrl(path) {
    if (path && typeof path === "object" && typeof path.link === "string")
      return path.link;
    return typeof path === "string" ? path : "";
  },
  getStorage() {
    const configured = import.meta.env.VUE_APP_FIREBASE_STORAGE_BUCKET;
    if (!configured) return $firebase.app().storage("gs://katana-main");
    return $firebase.app().storage(configured.startsWith("gs://") ? configured : `gs://${configured}`);
  },
  /**
   * Remove all iterations of a given image
   * @param {String} path
   */
  removeImgFromStorage: function(path) {
    const sizes = listUtils.imgSizes;
    const storage = this.getStorage();

    for (const size of sizes)
      storage
        .ref(`${path}_${size}x${size}`)
        .delete()
        .catch(() => {});
  },
  /**
   * Returns a string from a provided date and format
   * @param {Date} date
   * @param {String} format
   * @returns {String}
   */
  getDateFormat(date, format = "DD/MM/YYYY à HH:mm") {
    if (!date) return "";
    const value = typeof date.toDate === "function" ? date.toDate() : date;
    return _moment(value).format(format);
  },
  /**
   * Returns if a user can access a module
   * @param {String} user
   * @param {String} module
   * @returns {Boolean}
   */
  hasModule: async function(user, module) {
    const { data } = await apiUser({ url: `/companies/${user.company}` });
    return (data.modules || []).some((item) => item.id === module);
  },
  /**
   * Open a new tab with the provided link
   * @param {String} link
   * @param {String} param
   */
  goTo: function(link, param = "_blank") {
    window.open(link, param);
  },

  /**
   * Returns the position of a item with a specific attribute in an array
   * @param {Array} array
   * @param {String} attr
   * @param {String} value
   * @returns {Number}
   */
  findWithAttr,

  /**
   * Gets the next index of a provided array
   * @param {Array} array
   * @param {Number} n
   * @returns {Number}
   */
  getNextIndex: function(array, n) {
    if (!array?.length) return 1;

    const numbers = array
      .map((item) => Number(item[n]))
      .filter((value) => !Number.isNaN(value));

    if (!numbers.length) return 1;

    const max = Math.max(...numbers);
    return max > 0 ? max + 1 : 1;
  },

  uniqueReference: function(existing = []) {
    const used = new Set((existing || []).filter(Boolean));
    let reference;
    do
      reference = Math.random().toString(36).substring(2, 8).toUpperCase();
    while (used.has(reference));
    return reference;
  },

  /**
   * Returns a hexadecimal from a provided string
   * @param {String} str
   * @returns {String<Hexa>}
   */
  convertToHex: function(str) {
    let result = "";
    if (!str) return;
    for (let i = 0; i < 3; i++)
      result += str.codePointAt(i).toString(16);

    return result;
  },

  /**
   * Gets a number within 0 and provided size
   * @param {Number} size
   * @returns {Number}
   */
  randomNumbers: function(size) {
    let result = "";
    for (let i = 0; i < size; i++)
      result += String(Math.floor(Math.random() * 10));

    return result;
  },

  truncateString(str, max, suffix = "...") {
    return str.length < max ? str : `${str.substr(0, str.substr(0, max - suffix.length).lastIndexOf(" "))}${suffix}`;
  },
};
